import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, OnDestroy, OnInit, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { VisitDetailPhotos } from '@types-lib';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { BehaviorSubject, catchError, EMPTY, from, map, Observable, of, Subscription, tap } from 'rxjs';
import { Constants } from '../../../../../../../../../../angularJs/src/app/services/constants.service';
import { Notifications } from '../../../../../../../../../../angularJs/src/app/services/notification.service';
import { STATUS } from '../../../../../../../../../../angularJs/src/app/services/order.service';
import { initialiseCounts, initialiseTask } from '../../../../../../../../types/photo';
import { SecureMessageDialogComponent } from '../../components/secure-message/secure-message-dialog.component';
import { TaskPhotosComponent } from '../task-photos/task-photos.component';
import { AiDermComponent } from './ai-derm/ai-derm.component';
// import {
// 	MultiselectComponent
// } from '../../../../../../../../../../../../libs/ui-core/src/lib/ui-components/multiselect/multiselect.component';
import { AdditionalExamPhotosEndpoint, ExamPhotosReviewsEndpoint } from '@pims-endpoints';
import { ServiceUpgradeInjectorService } from '@shims';
import { VisitDetailPhotoStore } from '@visit-detail-photos-store';

@Component({
  selector: 'exam-photos',
  imports: [CommonModule, MatFormField, MatSelect, MatOption, TaskPhotosComponent, AiDermComponent],
  providers: [VisitDetailPhotoStore],
  templateUrl: './exam-photos.component.html',
  styleUrls: ['./exam-photos.component.scss'],
})
export class ExamPhotosComponent implements OnInit, OnDestroy {
  readonly visitDetailPhotoStore = inject(VisitDetailPhotoStore);
  @Input() visit: any = {};
  private env = inject<PIMSEnvironment>(ENVIRONMENT);
  private dialog = inject(MatDialog);
  private Constants = inject(Constants);
  private Notifications = inject(Notifications);
  private ServiceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  private snackBar = inject(SnackNotificationsDialogService);
  ExamPhotoReviews = new ExamPhotosReviewsEndpoint();
  ExamPhotoAdditional = new AdditionalExamPhotosEndpoint();
  BOD_858_feature_flag = FEATURE_BOD_858;
  BOD_3536 = FEATURE_BOD_3536;
  loading = false;
  task: VisitDetailPhotos.Task = initialiseTask();
  states: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  state = 0;
  errorMessage: string | null = null;
  isVisitSCD = computed(() => this.visit.service?.code === 'SCD-F');
  photos: Signal<VisitDetailPhotos.Photo[]> = this.visitDetailPhotoStore.examPhotosWithNoDuplicateFiles;
  errorMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  diagnosis: Signal<string | null> = this.visitDetailPhotoStore.diagnosis;
  cannotRequestExamPhoto = true;
  types$: Observable<VisitDetailPhotos.IdTypes[]> = from([]);
  counts$: Observable<VisitDetailPhotos.Counts> = of(initialiseCounts());
  // remainingRequests$: Observable<number> = of(4)
  counts: VisitDetailPhotos.Counts = initialiseCounts();
  remainingRequests$: Observable<number> = of(1);
  photoStatuses = {
    1: 'Previously requested photos are yet to be uploaded.',
    2: 'You cannot request until previous photos have been approved.',
  };
  photoStatus: string | null = null;
  errorMessages: VisitDetailPhotos.ErrorMessages = {
    400: '',
    403: '',
  };
  subscriptions = new Subscription();

  examPhotoApproved = 0;
  examPhotoRejected = 0;

  ngOnInit() {
    this.visitDetailPhotoStore.loadExamPhotos(this.visit.id);
    this.examPhotoApproved = this.Constants.AdminStatuses.ExamPhotoApproved.NumericValue;
    this.examPhotoRejected = this.Constants.AdminStatuses.ExamPhotoRejected.NumericValue;
  }

  reload() {
    this.visitDetailPhotoStore.loadExamPhotos(this.visit.id);
  }

  openMessageDialog(): void {
    const dialogRef = this.dialog.open(SecureMessageDialogComponent, { disableClose: true });
    this.subscriptions.add(
      dialogRef.afterClosed().subscribe((result: string) => {
        if (result) {
          const payload = {
            request_note: result,
            number: this.state,
          };
          this.requestAdditionalPhotos(this.visit.id, payload);
        }
      }),
    );
  }

  approveExamPhotos(value: VisitDetailPhotos.PropsPayload) {
    const { payload } = value;
    const reviews = payload.photos.map((photo: VisitDetailPhotos.Photo) => {
      return {
        file_id: photo.file.id,
        approved: true,
      };
    });
    this.updatePhotoTask({ reviews }, true);
  }

  rejectExamPhotos(value: VisitDetailPhotos.PropsPayload) {
    const { payload } = value;
    const reviews = payload.photos
      .filter((photo: VisitDetailPhotos.Photo) => photo.rejected)
      .map((photo: VisitDetailPhotos.Photo) => {
        return {
          file_id: photo.file.id,
          approved: false,
          rejection_note: photo.file.rejection_note,
        };
      });
    this.updatePhotoTask({ reviews }, false);
  }

  updatePhotoTask(payload: VisitDetailPhotos.ExamPhotoPayload, approved: boolean) {
    this.subscriptions.add(
      this.ExamPhotoReviews.post(this.visit.id, payload)
        .pipe(
          tap(() => this.handleUpdatePhotoTaskSuccess(approved)),
          catchError((error: any) => {
            console.log(error);
            return this.mapUpdatePhotoError(error).pipe(catchError(() => EMPTY));
          }),
        )
        .subscribe(() => {
          this.visitDetailPhotoStore.loadExamPhotos(this.visit.id);
        }),
    );
  }

  handleUpdatePhotoTaskSuccess(approved: boolean) {
    this.task.status = approved ? STATUS.APPROVED : STATUS.REJECTED;
    this.visit.admin_status = approved ? this.examPhotoApproved : this.examPhotoRejected;
    this.ServiceUpgradeInjectorService.getAngularJsRootScope().$broadcast('photos:updated', approved);
    this.ServiceUpgradeInjectorService.getAngularJsRootScope().$broadcast('visit:refresh');
  }

  mapUpdatePhotoError(error: any) {
    const errorMessage =
      this.errorMessages[error?.data?.status as keyof VisitDetailPhotos.ErrorMessages] ||
      (error?.data?.message && error?.data?.message.summary) ||
      this.Notifications.orders.photos.failed;
    this.errorMessage$.next(errorMessage);
    return of(errorMessage);
  }

  sendState(state: number) {
    this.state = state;
    console.log(this.state, 'state receieved');
  }

  approveAdditionalExamPhotos() {
    console.log(this.states, 'additional photos approve');
  }

  requestAdditionalPhotos(visitId: number, payload: VisitDetailPhotos.RequestAdditionalExamPhoto) {
    this.subscriptions.add(
      this.ExamPhotoAdditional.post(visitId, payload)
        .pipe(
          tap(() => {
            this.photoStatus = this.photoStatuses[1];
            this.cannotRequestExamPhoto = true;
            this.remainingRequests$ = this.remainingRequests$.pipe(map(data => data - 1));
          }),
          map(() => {
            this.snackBar.openSuccessMessage('Request for additional Exam Photos sent.');
          }),
          catchError((error: any) => {
            const errorMessage = error.data?.message?.summary || 'Error sending request for additional ID Photos.';
            this.snackBar.openWarningMessage(errorMessage);
            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
